import { Component, inject, Input, input } from '@angular/core';
import { ProviderSearchResultsCardComponent } from "../provider-search-results-card/provider-search-results-card.component";
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { SearchStateService } from '../../shared/services/search-state.service';
import { ProviderSearchFilters, SearchFilter } from '../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LocationCoordinates } from '../../../shared/model/LocationCoordinates';
import { MCAppGatewayService } from '../../../shared/services/mcappgateway/mcappgateway.service';
import { PostGetProviderSearchRequest } from '../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { TranslateService } from '@ngx-translate/core';
import { MonitoringService } from '../../../shared/services/monitoring/monitoring-service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-provider-search-results',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatMenuModule, MatRadioModule, ProviderSearchResultsCardComponent, MatProgressSpinner, MatAutocompleteModule],
  templateUrl: './provider-search-results.component.html',
  styleUrl: './provider-search-results.component.css'
})
export class ProviderSearchResultsComponent {

  searchState = inject(SearchStateService);
  mcAppGateway = inject(MCAppGatewayService);
  
  @Input() windowResolution: number = 0;
  @Input() locale: string = '';
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() cityTypeAheadOptions: string[] = [];

  showSpinner$ = this.searchState.showSpinner$;
  providerSearchQuery$ = this.searchState.providerSearchQuery$;
  providerSearchResults$ = this.searchState.providerSearchResults$;
  providerSearchFilters$ = this.searchState.providerSearchFilters$;
  providerSearchFilterToggle$ = this.searchState.providerSearchFilterToggle$;
  providerSortMethod$ = this.searchState.providerSortMethod$;

  showSpinner: boolean = false;
  allResults: any[] = [];
  currentFilters: ProviderSearchFilters | null = null; 
  queryNameOrSpecialty: string = '';
  queryCityOrZip: string = '';
  appliedFilterCount: number = 0;
  distanceSliderValue: number = 2; // Default value for distance slider
  providerSortMethod: 'distance' | 'availability' | 'name' = 'distance';
  
  // Pagination properties
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 1;
  visiblePages: number[] = [];

  // Typeahead properties
  cityOrZipControl = new FormControl('');
  filteredCityOptions$: Observable<string[]> = new Observable<string[]>();

  newSearchBtnText = "New Search";
  breadcrumbText = "Provider Search";
  breadcrumbText2 = "Search Results";
  resultsText = "Results";
  sortByTitle = "Sort by";
  distanceLabel = "Distance";
  availabilityLabel = "Soonest Availability";
  nameLabel = "Name (A-Z)";
  mileText = "mile";
  clearBtnText = "Clear";
  applyBtnText = "Apply";
  locationLabel = "Location";
   
  constructor(private translate: TranslateService, private monitoringService: MonitoringService) {
    this.locale =  sessionStorage.getItem('locale') || 'en-US';
    this.translate.use(this.locale); // Default to 'en-US' if no locale is provided
  }

  ngOnInit(): void {
    this.showSpinner$.subscribe(show => {
      this.showSpinner = show;
    });

    // Load translations
    this.translate.get('NEW SEARCH').subscribe((translated: string) => {
      this.newSearchBtnText = translated;
    });

    this.translate.get('PROVIDER_SEARCH').subscribe((translated: string) => {
      this.breadcrumbText = translated;
    });

    this.translate.get('SEARCH_RESULTS').subscribe((translated: string) => {
      this.breadcrumbText2 = translated;
    });

    this.translate.get('RESULTS').subscribe((translated: string) => {
      this.resultsText = translated;
    });

    this.translate.get('SORT_BY.TITLE').subscribe((translated: string) => {
      this.sortByTitle = translated;
    });

    this.translate.get('SORT_BY.DISTANCE').subscribe((translated: string) => {
      this.distanceLabel = translated;
    });

    this.translate.get('SORT_BY.AVAILABILITY').subscribe((translated: string) => {
      this.availabilityLabel= translated;
    });

    this.translate.get('SORT_BY.NAME').subscribe((translated: string) => {
      this.nameLabel = translated;
    });       

    this.translate.get('MILE').subscribe((translated: string) => {
        this.mileText = translated;
    });

    this.translate.get('APPLY').subscribe((translated: string) => {
      this.applyBtnText = translated;
    });

    this.translate.get('CLEAR').subscribe((translated: string) => {
      this.clearBtnText = translated;
    });

    this.translate.get('LOCATION').subscribe((translated: string) => {
      this.locationLabel = translated;
    });

    this.providerSearchQuery$.subscribe(query => {
      if (query) {
        this.queryNameOrSpecialty = query.nameOrSpecialty;
        this.queryCityOrZip = query.cityOrZip;
        this.cityOrZipControl.setValue(query.cityOrZip, { emitEvent: false });
      }
    });

    this.windowResolution = window.innerWidth;

    addEventListener('resize', () => {
      this.windowResolution = window.innerWidth;
    });

    this.providerSearchFilters$.subscribe(filters => {
      this.currentFilters = filters;
      // Calculate number of applied filters
      // any time the Apply filter button is pressed in the filter component
      let count = 0;

      console.log('Calculating applied filters from:', filters);
      if (filters) {
        if (filters.affiliationList) {
          count += filters.affiliationList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        if (filters.genderList) {
          count += filters.genderList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        if (filters.specialtyList) {
          count += filters.specialtyList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        if (filters.onlineSchedulingList) {
          count += filters.onlineSchedulingList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        if (filters.languageList) {
          count += filters.languageList.filter((option: SearchFilter) => option.isSelected == true).length;
        }

        console.log('Applied filter count:', count);
        this.appliedFilterCount = count;

        if (this.currentFilters &&  this.currentFilters.distanceInMiles && this.currentFilters.distanceInMiles > 0) {
          this.setDistanceFilterValue(this.currentFilters.distanceInMiles);
        } 
      }
    });

    // Subscribe to results to calculate pagination
    this.providerSearchResults$.subscribe(results => {
      if (results) {
        this.allResults = results;
        this.totalPages = Math.ceil(results.length / this.itemsPerPage);
        this.currentPage = 1; // Reset to first page when results change
        this.updateVisiblePages();
      }
    });

    this.providerSortMethod$.subscribe(sortMethod => {
      this.providerSortMethod = sortMethod;
    });

    // Setup autocomplete filtering for city/zip
    this.filteredCityOptions$ = this.cityOrZipControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCityOptions(value || ''))
    );

    // Sync the FormControl with queryCityOrZip
    this.cityOrZipControl.valueChanges.subscribe(value => {
      this.queryCityOrZip = value || '';
    });
  }

  private _filterCityOptions(value: string): string[] {
    // Only show suggestions if 3 or more characters have been typed
    if (!value || value.length < 3) {
      return [];
    }

    if (!this.cityTypeAheadOptions || !Array.isArray(this.cityTypeAheadOptions)) {
      console.log('No city type ahead options available');
      return [];
    }

    // Remove special characters and convert to lowercase for comparison
    const normalizeString = (str: string) => str.toLowerCase().replace(/[^a-z0-9\s]/gi, '');
    const filterValue = normalizeString(value);
    
    const filtered = this.cityTypeAheadOptions.filter(option => 
      normalizeString(option).includes(filterValue)
    );
    console.log('Filtered city options:', filtered);
    return filtered;
  }

  getPaginatedResults(): any[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.allResults.slice(startIndex, endIndex);
  }

  updateVisiblePages(): void {
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);
    
    // Adjust start if we're near the end
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    this.visiblePages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updateVisiblePages();
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  setDistanceFilterValue(distanceInMiles: number): void {
    // Map distance in miles to slider value
      switch (distanceInMiles) {
        case 5:
          this.distanceSliderValue = 0;
          break;
        case 10:
          this.distanceSliderValue = 1;
          break;
        case 15:
          this.distanceSliderValue = 2;
          break;
        case 25:
          this.distanceSliderValue = 3;
          break;
        case 50:
          this.distanceSliderValue = 4;
          break;
        default:
          this.distanceSliderValue = 2; // Default to 15 miles
      }
    console.log('Distance slider value set to:', this.distanceSliderValue);
  }

  getDistanceInMilesFromSliderValue(sliderValue: number): number {
    switch (sliderValue) {
      case 0:
        return 5;
      case 1:
        return 10;
      case 2:
        return 15;
      case 3:
        return 25;
      case 4:
        return 50;
      default:
        return 15; // Default to 15 miles
    }
  }

  setDistanceSliderValueFromLabel(value: number): void {
    this.distanceSliderValue = value;
  }

  onClearLocationFilter() {
    this.queryCityOrZip = this.currentFilters?.location || '';
    this.cityOrZipControl.setValue(this.queryCityOrZip, { emitEvent: false });

    if (this.currentFilters &&  this.currentFilters.distanceInMiles && this.currentFilters.distanceInMiles > 0) {
      this.setDistanceFilterValue(this.currentFilters.distanceInMiles);
    }
  }

  onApplySortMethod(sortBy: 'distance' | 'availability' | 'name') {
    this.searchState.setProviderSortMethod(sortBy);
    this.searchState.setProviderSearchResults(this.searchState.sortProviderSearchResults(this.allResults, sortBy));
  }

  onLocationMenuClosed() {
    // Reset any temporary changes made in the location filter menu
    this.onClearLocationFilter();
  }

  onApplyLocationFilter() {

    if (this.currentFilters) {
      var request: PostGetProviderSearchRequest = new PostGetProviderSearchRequest();

      request.providerName = this.currentFilters.providerName;

      if ((request.providerName == null || request.providerName == '') && this.queryNameOrSpecialty != '') {
        request.nameOrSpecialty = this.queryNameOrSpecialty;
      }
      request.cityOrZip = this.queryCityOrZip;
      request.distanceInMiles = this.getDistanceInMilesFromSliderValue(this.distanceSliderValue) ?? 15;      

      request.specialties = this.currentFilters?.specialtyList?.filter(s => s.isSelected).map(s => s.value).join(', ');
      request.onlineScheduling = this.currentFilters?.onlineSchedulingList?.some(o => o.isSelected) ? true : undefined;
      request.affiliations = this.currentFilters?.affiliationList?.filter(a => a.isSelected).map(a => a.value).join(', ');
      request.gender = this.currentFilters?.genderList?.find(g => g.isSelected)?.value ?? '';
      request.languages = this.currentFilters?.languageList?.filter(l => l.isSelected).map(l => l.value).join(', ');
      
      // only set the lattitude and longitude if a cityOrZip value is not provided
      if ((request.cityOrZip == null || request.cityOrZip == "") && this.currentLocation != null && this.currentLocation.latitude && this.currentLocation.longitude ) {
        request.latitude = this.currentLocation.latitude;
        request.longitude = this.currentLocation.longitude;
      }

      this.searchState.setShowSpinner(true);
      // call search api 
      this.mcAppGateway.PostGetProviderSearch(request).subscribe({
        next: (response) => {
          
          if (this.currentFilters != null) {
            this.currentFilters.location = request.cityOrZip ?? '';
            this.currentFilters.distanceInMiles = request.distanceInMiles ?? 15;
            this.searchState.setProviderSearchFilters(this.currentFilters);
            console.log('Applied location filter:', this.currentFilters.location, 'Distance in miles:', this.currentFilters.distanceInMiles);
          
            this.searchState.setProviderSearchQuery({
              nameOrSpecialty: this.currentFilters.providerName != '' ? this.currentFilters.providerName : this.queryNameOrSpecialty,
              cityOrZip: this.queryCityOrZip,
            });
          }

          var sortedResponse = this.searchState.sortProviderSearchResults(response || [], this.providerSortMethod);
          console.log('Provider Search Response after applying location filter:', sortedResponse);
          this.searchState.setProviderSearchResults(sortedResponse);
          this.searchState.setShowSpinner(false);
        },
        error: (error) => {
          console.error('Error fetching provider search results after applying location filter:', error);
          alert('An error occurred while fetching provider search results. Please try again later.');
          this.searchState.setShowSpinner(false);
        }
      });     
    }
  }

  clearSearch(): void {
    
    this.searchState.setProviderSearchQuery({ nameOrSpecialty: "", cityOrZip: this.queryCityOrZip })
    this.searchState.clearProviderSearchResults();
    this.clearFilters();
    this.searchState.setProviderSearchFilterToggle(false);
    this.searchState.clearCurrentProviderDetails()
  }

  clearFilters(): void {
    if (this.currentFilters) {
      // Clear all selections
      this.currentFilters.specialtyList?.forEach(option => option.isSelected = false);
      this.currentFilters.genderList?.forEach(option => option.isSelected = false);
      this.currentFilters.affiliationList?.forEach(option => option.isSelected = false);
      this.currentFilters.languageList?.forEach(option => option.isSelected = false);
      this.currentFilters.onlineSchedulingList?.forEach(option => option.isSelected = false);

      this.searchState.setProviderSearchFilters(this.currentFilters);
    }
  }
}